// LazyRadarPlot — viewport-aware wrapper for EChartsComponent
// Mounts ECharts when container is within 100px of viewport,
// unmounts when scrolled >2 viewports away (memory management for many charts on one page).

import { useState, useEffect, useRef } from 'preact/hooks'
import EChartsComponent from './EChartsComponent.jsx'
import '../../../css/components/lazy-radar-plot.css'

/**
 * Lazy-mounting wrapper around EChartsComponent.
 *
 * @param {Object} props
 * @param {Object} props.option - ECharts option object (passed through to EChartsComponent)
 * @param {Object} [props.style] - Inline styles for the outer container
 * @param {string} [props.className] - CSS class for the outer container
 * @param {Function} [props.onChartReady] - Callback when chart instance is ready
 * @param {string} [props.height='400px'] - Height of the chart skeleton / container
 */
export default function LazyRadarPlot({ option, style, className, onChartReady, height = '400px', notMerge = false }) {
  const containerRef = useRef(null)
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  // Mount observer: bring in EChartsComponent when container approaches viewport
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const mountObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsMounted(true)
        }
      },
      { rootMargin: '100px', threshold: 0 },
    )

    mountObserver.observe(el)

    return () => {
      mountObserver.disconnect()
    }
  }, [])

  // Dispose observer removed — in viewport-contained layout (one plot at a time),
  // unmounting is handled by React/Preact when the parent swaps the active plot.

  // Fade-in: slight delay after mount so the chart has time to initialize
  useEffect(() => {
    if (!isMounted) return

    const timerId = setTimeout(() => {
      setIsVisible(true)
    }, 50)

    return () => {
      clearTimeout(timerId)
    }
  }, [isMounted])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', height, ...style }}
    >
      {!isMounted ? (
        <div class="chart-skeleton" style={{ width: '100%', height }} />
      ) : (
        <div
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
            height: '100%',
          }}
        >
          <EChartsComponent option={option} style={{ height: '100%' }} onChartReady={onChartReady} notMerge={notMerge} />
        </div>
      )}
    </div>
  )
}
